// pages/FormViewerPage/services/formViewerService/fetch.ts — Form / product / warehouse fetchers
import { supabase } from '../../../../supabase/client';
import type { Form, MessageTemplates } from '../../../../types';
import { getCachedMessageTemplates } from '../../../../utils/cacheHelpers/brands';
import { getCachedFormBySlug, getCachedFormById } from '../../../../utils/cacheHelpers/forms';
import { getOptimizedImageUrl } from '../../../../utils/imageUtils';
import { normalizeForm } from '../../../../utils';
import { resolveSlugAlias } from '../../../../utils/slugAliases';
import { resolveVariantImageUrl } from '../../../../utils/variantImage';

export async function fetchMessageTemplates(): Promise<MessageTemplates | null> {
    try {
        const { data } = await getCachedMessageTemplates();
        if (data) {
            const { id, created_at, updated_at, ...rest } = data as any;
            return rest as MessageTemplates;
        }
    } catch { /* */ }
    return null;
}

export async function fetchBiteshipApiKey(form: Form | null): Promise<string> {
    // Biteship deprecated (2026-08-14) — Everpro satu-satunya provider.
    // TIDAK lagi membaca settings 'website' (baris itu admin-only + bawa secret).
    if (form?.biteshipSettings?.apiKey) return form.biteshipSettings.apiKey;
    return '';
}

// Panjang maksimum kolom forms.slug (varchar(60)) - dipakai fallback slug panjang.
const FORM_SLUG_MAX = 60;

// Bootstrap dari <head> (vite/entryHead.html → /api/form-public/:slug).
// Menghilangkan DNS+TLS Supabase + CORS preflight + 1 roundtrip DB dari jalur
// kritis form. Kalau promise belum siap / gagal / beda slug → null (fallback
// ke query Supabase lama, jadi tidak pernah lebih buruk dari sebelumnya).
const BOOT_TIMEOUT_MS = 1500;
export type LoadFormResult =
    | { foundForm: Form; redirectedTo?: string; notFound?: undefined; formInactive?: undefined }
    | { notFound: true; foundForm?: undefined; formInactive?: undefined }
    | { formInactive: true; foundForm?: undefined; notFound?: undefined };

async function readBootForm(slug: string): Promise<LoadFormResult | null> {
    if (typeof window === 'undefined') return null;
    const w = window as any;
    if (!w.__FORM_BOOT_P || !w.__FORM_BOOT_SLUG) return null;
    if (String(w.__FORM_BOOT_SLUG).toLowerCase() !== slug) return null;
    let payload: any = null;
    try {
        payload = await Promise.race([
            w.__FORM_BOOT_P,
            new Promise((resolve) => setTimeout(() => resolve(null), BOOT_TIMEOUT_MS)),
        ]);
    } catch { return null; }
    if (!payload) return null;
    if (payload.inactive) return { formInactive: true } as const;
    if (!payload.form) return { notFound: true } as const;
    return { foundForm: normalizeForm(payload.form as Form) } as const;
}

export async function loadFormData(identifier: string): Promise<LoadFormResult> {
    if (!identifier) return { notFound: true };
    let rawIdentifier: string;
    try { rawIdentifier = decodeURIComponent(identifier).trim(); } catch { return { notFound: true }; }
    const normalizedIdentifier = rawIdentifier.replace(/^\//, '').toLowerCase();
    const isUuid = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(normalizedIdentifier);
  if (!isUuid) {
    const booted = await readBootForm(normalizedIdentifier);
    // Boot bilang "tidak ada" BELUM final: slug lama (diganti pasca-publish)
    // perlu dicek ke alias + DB langsung di bawah. Hanya hasil positif/warning
    // yang langsung dipakai agar first-paint tetap cepat.
    if (booted && !booted.notFound) return booted;
  }
    let foundForm: Form | null = null;
    try {
        if (isUuid) {
            const r = await getCachedFormById(normalizedIdentifier, { withDescription: false });
            if (r.data) foundForm = r.data as Form;
            else { const sr = await getCachedFormBySlug(normalizedIdentifier, { withDescription: false }); if (sr.data) foundForm = sr.data as Form; }
        } else {
            const r = await getCachedFormBySlug(normalizedIdentifier, { withDescription: false });
            if (r.data) foundForm = r.data as Form;
            // Kolom forms.slug maksimal 60 karakter (schema), sedangkan slug LP bisa
            // lebih panjang -> link /f/<slug-panjang> dulu berakhir "Formulir tidak
            // ditemukan". Fallback ke 60 karakter pertama (bentuk yang tersimpan).
            if (!foundForm && normalizedIdentifier.length > FORM_SLUG_MAX) {
                const truncated = await getCachedFormBySlug(normalizedIdentifier.slice(0, FORM_SLUG_MAX), { withDescription: false });
                if (truncated.data) foundForm = truncated.data as Form;
            }
        }
    } catch { return { notFound: true }; }
    if (!foundForm) {
        // Slug lama (diganti pasca-publish)? Coba alias -> URL baru.
        const alt = await resolveSlugAlias('form', normalizedIdentifier);
        if (alt && alt.toLowerCase() !== normalizedIdentifier) {
            const r2 = await getCachedFormBySlug(alt, { withDescription: false });
            if (r2.data) {
                const f2 = r2.data as unknown as Form;
                if (f2.status === 'inactive') return { formInactive: true };
                return { foundForm: normalizeForm(f2), redirectedTo: (f2.slug || alt).toLowerCase() };
            }
        }
        return { notFound: true };
    }
    if (foundForm.status === 'inactive') return { formInactive: true };
    return { foundForm: normalizeForm(foundForm) };
}

export async function fetchProductData(productId: string) {
    try {
        const { data } = await supabase.from('products').select('name, attributes').eq('id', productId).maybeSingle();
        return data ? { name: data.name, attributes: data.attributes } : null;
    } catch { return null; }
}

export function preloadMainImage(imageUrl: string) {
    if (!imageUrl) return;
    // WAJIB identik dgn render (ProductImageSection 640/q70) & head preload
    // (public.html dkk 640/q70) — beda 1 char = browser download 2x.
    const optimizedUrl = getOptimizedImageUrl(imageUrl, { width: 640, quality: 70 });
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = optimizedUrl || imageUrl;
    document.head.appendChild(link);
}

// Kunci BUKAN varian (speed 2026-10-22): WAJIB sinkron dengan head snippet
// (public.html dkk), backend formMainImageVariant.js, dan effectsCore.ts —
// tambah kunci tracking baru = ubah SEMUA tempat.
const NON_VARIANT_KEYS = new Set([
    'platform', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term',
    'utm_content', 'fbclid', 'gclid', 'gbraid', 'wbraid', 'ttclid',
    'name', 'phone', 'qty', 'pay', 'addr', 'lead', 'type', 'embed', 'mode',
]);

/** Hero form = FOTO VARIAN bila URL preselect varian (mirror backend
 * formMainImageVariant.js + render FormViewer). Fallback mainImage. */
export function resolveFormHeroImage(form: {
    mainImage?: string | null;
    productOptions?: Array<{ name?: string }>;
    variantCombinations?: Array<{ attributes?: Record<string, string>; imageUrl?: string }>;
    productVariants?: Array<{ name?: string; optionValues?: string[]; imageUrl?: string }>;
} | null): string | null {
    if (!form) return null;
    try {
        const params = new URLSearchParams(window.location.search);
        const names = new Set((form.productOptions || []).map(o => o?.name).filter(Boolean));
        const selected: Record<string, string> = {};
        params.forEach((v, k) => {
            if (NON_VARIANT_KEYS.has(k) || k === 'variant') return;
            if (names.has(k) && v.trim()) selected[k] = v.trim();
        });
        const leadVariant = (params.get('variant') || '').trim();
        if (Object.keys(selected).length > 0) {
            const hit = resolveVariantImageUrl(form.variantCombinations, selected, form.productVariants);
            if (hit) return hit;
        }
        // ?variant= (prefill CS): cocok ke atribut mana pun (mirror backend).
        if (leadVariant) {
            const combos = form.variantCombinations || [];
            const c = combos.find(x => Object.values(x?.attributes || {}).includes(leadVariant));
            if (c?.imageUrl) return c.imageUrl;
            const pv = (form.productVariants || []).find(p =>
                (Array.isArray(p.optionValues) && p.optionValues.includes(leadVariant)) || p.name === leadVariant);
            if (pv?.imageUrl) return pv.imageUrl;
        }
    } catch { /* abaikan — fallback mainImage */ }
    return form.mainImage || null;
}
